// R7 — normalise a wire error into the catalog-friendly WireError shape.
//
// Three error carriers exist on the wire during the R7 rollout:
//   1. envelope.payload.errorEvent  — the typed ErrorEvent (field 570). Preferred.
//   2. envelope.error               — the legacy ErrorResponse (back-compat). Its
//      `details.error_code` carries the enum NAME and `details.retryable` the hint.
//   3. an HTTP JSON body            — { error, error_code, retryable } (snake_case).
//
// This module collapses (1) and (2) into one WireError so every FE surface can
// hand it straight to toastStore.showError without knowing which carrier the
// server used.

import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import { ErrorCode } from '@/lib/protobuf/gen/errors/errors_pb';
import type { ValidationIssue } from '@/lib/protobuf/gen/errors/errors_pb';
import { type WireError, type WireIssue } from '@/lib/errors/catalog';
import { limitUnitFromProto } from '@/lib/limits/types';

// Map an enum NAME (as carried in the legacy details map, e.g.
// "ERROR_CODE_BUDGET_EXHAUSTED") to the numeric ErrorCode. protobuf-es strips
// the ERROR_CODE_ prefix from the TS enum keys, so we re-derive it here.
function codeFromName(name: string | undefined): ErrorCode | undefined {
  if (!name) return undefined;
  const key = name.replace(/^ERROR_CODE_/, '');
  const val = (ErrorCode as Record<string, unknown>)[key];
  return typeof val === 'number' ? (val as ErrorCode) : undefined;
}

/** Reverse lookup: numeric ErrorCode → its enum NAME, when this bundle has one. */
function nameFromCode(code: number | undefined): string | undefined {
  if (code == null) return undefined;
  const name = (ErrorCode as unknown as Record<number, string | undefined>)[code];
  return typeof name === 'string' ? name : undefined;
}

/** Copy a proto ValidationIssue into the plain WireIssue shape (WF-38). */
export function wireIssueFromProto(is: ValidationIssue): WireIssue {
  return {
    path: is.path,
    code: is.code,
    params: { ...(is.params ?? {}) },
    message: is.message,
    severity: is.severity,
  };
}

/**
 * wireErrorFromEnvelope extracts a WireError from a MessageEnvelope, or returns
 * null if the envelope carries no error at all. Prefers the typed ErrorEvent;
 * falls back to the legacy ErrorResponse + its details map.
 */
export function wireErrorFromEnvelope(env: MessageEnvelope): WireError | null {
  // (1) Typed ErrorEvent on the payload oneof.
  if (env.payload?.case === 'errorEvent') {
    const ev = env.payload.value;
    const wire: WireError = {
      code: ev.code,
      message: ev.message,
      retryable: ev.retryable,
    };
    // Carry the enum's own name when this bundle knows it, so code-name
    // consumers work off either carrier. A newer backend code that this
    // bundle's enum lacks resolves to undefined here and is matched on
    // `message` instead.
    const name = nameFromCode(ev.code);
    if (name) wire.codeName = name;
    // BILL-20a (241): ErrorEvent fields 6/7. proto3 strings default to "",
    // which means "not set" here.
    if (ev.feature) wire.feature = ev.feature;
    if (ev.requiredTier) wire.requiredTier = ev.requiredTier;
    // ErrorEvent field 8: the number a refusal enforced, for every limit code
    // (BILL-10, CHAT-71, LIMIT-03). proto3 int32 defaults to 0, which means
    // "not set" here.
    if (ev.limit) wire.limit = ev.limit;
    // LIMIT-03: fields 10/11. LIMIT_UNIT_UNSPECIFIED (0) and current 0 mean "not set".
    const unit = limitUnitFromProto(ev.limitUnit);
    if (unit) wire.limitUnit = unit;
    const current = Number(ev.current);
    if (current > 0) wire.current = current;
    // CHAT-71 field 9 and LIMIT-07 field 13: the wait and the window. 0 = not set.
    if (ev.retryAfterSeconds) wire.retryAfterSeconds = ev.retryAfterSeconds;
    if (ev.limitWindowSeconds) wire.windowSeconds = ev.limitWindowSeconds;
    // WF-38: the field a VALIDATION refusal is about (field 3) and its named
    // issues (field 12), so the workflow editors can render each at its path.
    if (ev.field) wire.field = ev.field;
    // Optional chaining: a hand-built ErrorEvent (tests, older fixtures) may
    // carry no issues array at all.
    if (ev.issues?.length) wire.issues = ev.issues.map(wireIssueFromProto);
    return wire;
  }

  // (2) Legacy ErrorResponse.
  const legacy = env.error;
  if (legacy && (legacy.message || legacy.code)) {
    const details = legacy.details ?? {};
    const code = codeFromName(details['error_code']);
    // Build conditionally — exactOptionalPropertyTypes forbids assigning an
    // explicit `undefined` to an optional field.
    const wire: WireError = {};
    if (code !== undefined) wire.code = code;
    if (legacy.message) wire.message = legacy.message;
    if (details['retryable'] === 'true') wire.retryable = true;
    // Raw code name, kept even when this bundle's enum has no member for it.
    const rawName = details['error_code'] ?? details['code'];
    if (rawName) wire.codeName = rawName;
    // WF-38: the legacy carrier names the field only; it has no issues.
    if (details['field']) wire.field = details['field'];
    return wire;
  }

  return null;
}

/**
 * hasWireError reports whether an envelope carries an error in either carrier.
 */
export function hasWireError(env: MessageEnvelope): boolean {
  return env.payload?.case === 'errorEvent' || !!(env.error && (env.error.message || env.error.code));
}
